import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/components/shared/page-header'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  MoreHorizontal,
  ShieldAlert,
  Trash2,
  UserPlus,
  CheckCircle2,
  Mail,
  KeyRound,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Card } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useSession } from '@/lib/auth-client'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787'

export const Route = createFileRoute('/_app/settings/team')({
  component: TeamSettingsPage,
})

const inviteSchema = z.object({
  email: z.string().email('Email inválido'),
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres').optional(),
})

type InviteFormValues = z.infer<typeof inviteSchema>

type TeamMember = {
  id: string
  name: string
  email: string
  createdAt: string
  updatedAt: string
  lastActiveAt: string | null
}

function timeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMinutes < 1) return 'agora mesmo'
  if (diffMinutes < 60) return `há ${diffMinutes}min`
  if (diffHours < 24) return `há ${diffHours}h`
  if (diffDays < 30) return `há ${diffDays}d`
  return date.toLocaleDateString('pt-BR')
}

function TeamSettingsPage() {
  const queryClient = useQueryClient()
  const { data: session } = useSession()
  const currentUserId = session?.user?.id

  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null)

  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: '',
      name: '',
    },
  })

  // Queries
  const { data, isLoading } = useQuery<{ data: TeamMember[] }>({
    queryKey: ['merchant-users'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      if (!response.ok) throw new Error('Falha ao carregar equipe')
      return response.json()
    },
  })

  // Mutations
  const inviteMutation = useMutation({
    mutationFn: async (values: InviteFormValues) => {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/invite`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      })

      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.error || 'Erro ao convidar usuário')
      }
      return response.json()
    },
    onSuccess: () => {
      toast.success('Convite enviado com sucesso')
      setIsInviteOpen(false)
      form.reset()
      queryClient.invalidateQueries({ queryKey: ['merchant-users'] })
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.error || 'Erro ao remover usuário')
      }
      return response.json()
    },
    onSuccess: () => {
      toast.success('Acesso revogado com sucesso')
      setMemberToRemove(null)
      queryClient.invalidateQueries({ queryKey: ['merchant-users'] })
    },
    onError: (error) => {
      toast.error(error.message)
      setMemberToRemove(null)
    },
  })

  const resendInviteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/resend-invite`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.error || 'Erro ao reenviar convite')
      }
      return response.json()
    },
    onSuccess: () => {
      toast.success('Convite reenviado com sucesso')
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.error || 'Erro ao resetar senha')
      }
      return response.json()
    },
    onSuccess: () => {
      toast.success('E-mail de redefinição de senha enviado')
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const onSubmit = (values: InviteFormValues) => {
    inviteMutation.mutate(values)
  }

  const teamMembers = data?.data || []

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        size="sm"
        title="Equipe"
        description="Gerencie quem tem acesso à sua loja."
        actions={
          <ChonkyButton onClick={() => setIsInviteOpen(true)}>
            <UserPlus className="h-3.5 w-3.5" />
            Convidar
          </ChonkyButton>
        }
      />

      <Card>
        <div className="rounded-md border border-border/50 overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead className="bg-muted/50 text-muted-foreground border-b border-border/50">
              <tr>
                <th className="px-4 py-3 font-medium">Usuário</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Último acesso</th>
                <th className="px-4 py-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                    Carregando...
                  </td>
                </tr>
              ) : teamMembers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <ShieldAlert className="h-6 w-6 text-primary" />
                      </div>
                      <p className="text-foreground font-medium">Nenhum membro na equipe</p>
                      <p className="text-sm text-muted-foreground">
                        Adicione membros para ajudarem a gerenciar sua loja.
                      </p>
                      <ChonkyButton
                        variant="secondary"
                        className="mt-2"
                        onClick={() => setIsInviteOpen(true)}
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Convidar Primeiro Membro
                      </ChonkyButton>
                    </div>
                  </td>
                </tr>
              ) : (
                teamMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center font-medium text-primary shrink-0">
                          {member.name
                            ? member.name[0].toUpperCase()
                            : member.email[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{member.name || 'Sem nome'}</p>
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>Ativo</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-sm">
                      {member.lastActiveAt ? timeAgo(member.lastActiveAt) : 'Nunca acessou'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <span className="sr-only">Ações</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Ações</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {member.id !== currentUserId && (
                            <>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                disabled={resendInviteMutation.isPending}
                                onClick={() => resendInviteMutation.mutate(member.id)}
                              >
                                <Mail className="mr-2 h-4 w-4" />
                                Reenviar Convite
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                disabled={resetPasswordMutation.isPending}
                                onClick={() => resetPasswordMutation.mutate(member.id)}
                              >
                                <KeyRound className="mr-2 h-4 w-4" />
                                Resetar Senha
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive cursor-pointer"
                                onClick={() => setMemberToRemove(member)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Revogar Acesso
                              </DropdownMenuItem>
                            </>
                          )}
                          {member.id === currentUserId && (
                            <DropdownMenuItem disabled>Você (Proprietário)</DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convidar Membro</DialogTitle>
            <DialogDescription>
              Convide um novo usuário para ajudar a gerenciar a sua loja. Eles receberão um e-mail
              com instruções.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input placeholder="usuario@empresa.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome (Opcional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Nome do usuário" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="pt-4">
                <ChonkyButton
                  type="button"
                  variant="secondary"
                  onClick={() => setIsInviteOpen(false)}
                >
                  Cancelar
                </ChonkyButton>
                <ChonkyButton type="submit" disabled={inviteMutation.isPending}>
                  {inviteMutation.isPending ? 'Enviando...' : 'Enviar Convite'}
                </ChonkyButton>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation */}
      <AlertDialog
        open={!!memberToRemove}
        onOpenChange={(open) => !open && setMemberToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revogar acesso</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover o acesso de <strong>{memberToRemove?.email}</strong>?
              Este usuário perderá o acesso a esta loja imediatamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => memberToRemove && removeMutation.mutate(memberToRemove.id)}
              disabled={removeMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeMutation.isPending ? 'Removendo...' : 'Sim, remover acesso'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
